/**
 * Wave 24 City Agency Pages — 10 new cities
 * Marseille, Leipzig, Gdańsk, Sofia Hub, Vilnius Hub, Pune, Jakarta Hub, Santiago Hub, Kigali Hub, Kuwait City Hub
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling. React, Node.js, Python, cloud-native.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools. Modern stack with React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring, and 24/7 managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists, and project managers integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance (GDPR, HIPAA, Enterprise Security Standards).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools, and banking applications with enterprise-grade security.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management, and omnichannel retail solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine platforms, EHR integrations, and HIPAA-compliant health applications.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE24: Record<string, CityAgencyData> = {

  marseille: {
    slug: 'software-development-agency-marseille',
    cityName: 'Marseille',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Marseille | Call IT Dev',
      fr: 'Agence de Développement Informatique Marseille | Call IT Dev',
      es: 'Agencia Desarrollo Software Marsella | Call IT Dev',
      de: 'Softwareentwicklung Agentur Marseille | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Marseille. Custom SaaS, web, mobile, AI. French-speaking engineers, CET timezone, from €25/hr. 60% savings vs local rates.',
      fr: 'Agence de développement informatique pour Marseille. SaaS, web, mobile, IA. Ingénieurs francophones, fuseau CET, dès 25 €/h. 60% d\'économies vs tarifs locaux.',
      es: 'Agencia de desarrollo de software para Marsella. SaaS, web, móvil, IA. Ingenieros francófonos, desde 25 €/h.',
      de: 'Softwareentwicklungsagentur für Marseille. SaaS, Web, Mobile, KI. Französischsprachige Ingenieure, ab 25 €/h.',
    },
    keywords: ['software development agency Marseille', 'agence développement informatique Marseille', 'IT outsourcing Marseille', 'développeur Marseille', 'nearshore Marseille'],
    heroTitle: {
      en: 'Software Development Agency for Marseille',
      fr: 'Agence de Développement Informatique pour Marseille',
      es: 'Agencia de Desarrollo de Software para Marsella',
      de: 'Softwareentwicklung Agentur für Marseille',
    },
    heroSubtitle: {
      en: 'Mediterranean tech gateway. French-speaking engineers at nearshore rates. Same CET timezone, 2.5h flight from Casablanca, 60% savings.',
      fr: 'Porte tech de la Méditerranée. Ingénieurs francophones à tarifs nearshore. Même fuseau CET, 2h30 de vol depuis Casablanca, 60% d\'économies.',
      es: 'Puerta tech del Mediterráneo. Ingenieros francófonos a precios nearshore. Zona CET, 60% ahorro.',
      de: 'Mediterranes Tech-Tor. Französischsprachige Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Marseille Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Marseillaises Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Marsella Eligen Call IT Dev',
      de: 'Warum Marseiller Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🌊', title: 'Mediterranean Proximity', description: 'Marseille-Casablanca: 2.5h direct flight. Cultural proximity and shared Mediterranean business culture.' },
      { icon: '🗣️', title: 'Native French Engineers', description: 'All developers are fluent French speakers. No language barrier whatsoever.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior developers at €25-40/hr vs €70-100/hr in Marseille. Same quality, CET timezone.' },
      { icon: '🚢', title: 'Maritime & Logistics Tech', description: 'Marseille is France\'s port capital. Expertise in maritime, logistics, and supply chain software.' },
      { icon: '⏰', title: 'Identical CET Timezone', description: 'Same working hours as Marseille. Real-time collaboration all day.' },
      { icon: '🔒', title: 'RGPD Compliant', description: 'Full French data protection compliance. Enterprise-grade security.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How close is Casablanca to Marseille?', answer: '2.5h direct flight. Multiple weekly connections via Air France, Royal Air Maroc, and Transavia. One of the closest nearshore destinations.' },
      { question: 'Do your engineers speak French?', answer: 'Yes, all engineers are native French speakers (C2 level). Meetings, documentation, and code comments in French.' },
      { question: 'What is the cost vs Marseille agencies?', answer: 'Senior developers at €25-40/hr vs €70-100/hr locally. 60%+ savings with identical timezone.' },
      { question: 'Do you have maritime/port tech experience?', answer: 'Yes. We build logistics platforms, fleet management systems, and supply chain solutions for maritime companies.' },
      { question: 'Are you RGPD compliant?', answer: 'Yes. Enterprise-grade security, RGPD/GDPR compliant. DPA signed per CNIL guidelines.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days, first sprint in week 3.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '60%+',
  },

  leipzig: {
    slug: 'software-development-agency-leipzig',
    cityName: 'Leipzig',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Leipzig | Call IT Dev',
      fr: 'Agence de Développement Informatique Leipzig | Call IT Dev',
      de: 'Softwareentwicklung Agentur Leipzig | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Leipzig. Custom SaaS, web, mobile, AI. German-speaking engineers, CET timezone, from €25/hr. 60% savings vs Leipzig rates.',
      fr: 'Agence de développement informatique pour Leipzig. SaaS, web, mobile, IA. Ingénieurs germanophones, dès 25 €/h. 60% d\'économies.',
      de: 'Softwareentwicklungsagentur für Leipzig. SaaS, Web, Mobile, KI. Deutschsprachige Ingenieure, ab 25 €/h. 60% Ersparnis vs Leipziger Preise.',
    },
    keywords: ['software development agency Leipzig', 'Softwareentwicklung Leipzig', 'IT outsourcing Leipzig', 'nearshore development Leipzig', 'Softwareagentur Leipzig'],
    heroTitle: {
      en: 'Software Development Agency for Leipzig',
      fr: 'Agence de Développement Informatique pour Leipzig',
      de: 'Softwareentwicklung Agentur für Leipzig',
    },
    heroSubtitle: {
      en: 'East Germany\'s creative tech hub. German-speaking engineers at nearshore rates. CET timezone, 60% savings vs local agencies.',
      fr: 'Hub tech créatif d\'Allemagne de l\'Est. Ingénieurs germanophones à tarifs nearshore. Fuseau CET, 60% d\'économies.',
      de: 'Ostdeutschlands kreativer Tech-Hub. Deutschsprachige Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Leipzig Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Leipzig Choisissent Call IT Dev',
      de: 'Warum Leipziger Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🎨', title: 'Creative Tech Scene', description: 'Leipzig\'s vibrant startup and creative industries demand innovative software solutions.' },
      { icon: '🗣️', title: 'German-Speaking Teams', description: 'Native German speakers for all communication. No language barrier.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €25-40/hr vs €65-100/hr in Leipzig. Same quality, same timezone.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical working hours. Real-time collaboration and instant communication.' },
      { icon: '🏭', title: 'Industry 4.0 Expertise', description: 'Leipzig\'s automotive and manufacturing base — expertise in IoT and smart factory solutions.' },
      { icon: '🔒', title: 'BDSG & GDPR', description: 'Full German data protection compliance. Enterprise-grade security.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak German?', answer: 'Yes. Native German speakers assigned to all Leipzig projects. Documentation and code comments in German.' },
      { question: 'What is the cost vs Leipzig agencies?', answer: 'Senior developers at €25-40/hr vs €65-100/hr locally. 60%+ savings with CET timezone.' },
      { question: 'Do you have automotive/manufacturing experience?', answer: 'Yes. Leipzig is home to BMW and Porsche factories. We build IoT, MES, and Industry 4.0 solutions.' },
      { question: 'Are you GDPR/BDSG compliant?', answer: 'Yes. Enterprise-grade security, GDPR and BDSG compliant. AVV contracts per German requirements.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 days, first sprint in week 3.' },
      { question: 'Do you support startup projects?', answer: 'Yes. From MVP to Series C scaling. Flexible engagement models, no long-term commitment required.' },
    ],
    localRate: '€65-100',
    ourRate: '€25-40',
    savings: '60%+',
  },

  gdansk: {
    slug: 'software-development-agency-gdansk',
    cityName: 'Gdańsk',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (GMT+1)',
    currency: 'PLN/EUR',
    title: {
      en: 'Software Development Agency Gdańsk | Call IT Dev',
      fr: 'Agence de Développement Informatique Gdańsk | Call IT Dev',
      de: 'Softwareentwicklung Agentur Danzig | Call IT Dev',
      pl: 'Agencja Rozwoju Oprogramowania Gdańsk | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Gdańsk & Tri-City. SaaS, web, mobile, AI. CET timezone, from €20/hr. 65% savings vs Western Europe.',
      fr: 'Agence de développement informatique pour Gdańsk. SaaS, web, mobile, IA. Fuseau CET, dès 20 €/h. 65% d\'économies.',
      de: 'Softwareentwicklungsagentur für Danzig & Dreistadt. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 20 €/h. 65% Ersparnis.',
      pl: 'Agencja rozwoju oprogramowania Gdańsk i Trójmiasto. SaaS, web, mobile, AI. Strefa CET, od 20 €/h. 65% oszczędności.',
    },
    keywords: ['software development agency Gdańsk', 'IT outsourcing Gdańsk', 'nearshore development Tri-City', 'agencja IT Gdańsk', 'programiści Gdańsk'],
    heroTitle: {
      en: 'Software Development Agency for Gdańsk',
      fr: 'Agence de Développement Informatique pour Gdańsk',
      de: 'Softwareentwicklung Agentur für Danzig',
      pl: 'Agencja Rozwoju Oprogramowania dla Gdańska',
    },
    heroSubtitle: {
      en: 'Tri-City tech corridor. CET timezone, Baltic maritime expertise, 65% savings vs Western Europe. Poland\'s northern tech hub.',
      fr: 'Corridor tech de la Tri-Cité. Fuseau CET, expertise maritime balte, 65% d\'économies vs Europe de l\'Ouest.',
      de: 'Dreistadt-Tech-Korridor. CET-Zeitzone, baltische Maritime-Expertise, 65% Ersparnis vs Westeuropa.',
      pl: 'Korytarz technologiczny Trójmiasta. Strefa CET, morska ekspertyza bałtycka, 65% oszczędności vs Europa Zachodnia.',
    },
    whyTitle: {
      en: 'Why Choose Gdańsk for Software Development',
      fr: 'Pourquoi Choisir Gdańsk pour le Développement Logiciel',
      de: 'Warum Danzig für Softwareentwicklung Wählen',
      pl: 'Dlaczego Wybrać Gdańsk do Rozwoju Oprogramowania',
    },
    whyReasons: [
      { icon: '🚢', title: 'Maritime & Shipping Tech', description: 'Gdańsk is Poland\'s port capital. Engineers with deep maritime, logistics, and shipping software expertise.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Perfect alignment with Western European business hours.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior developers at €20-35/hr vs €80-120/hr in Western Europe.' },
      { icon: '🎓', title: 'Gdańsk Tech University', description: 'Top-ranked university producing high-caliber CS and engineering graduates.' },
      { icon: '🇪🇺', title: 'EU Member State', description: 'Full GDPR compliance, EU data residency, stable legal framework.' },
      { icon: '🏙️', title: 'Tri-City Talent Pool', description: 'Gdańsk-Sopot-Gdynia combined talent pool of 50K+ tech professionals.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Gdańsk over Warsaw?', answer: 'Lower costs, less competition for talent, and specialized maritime/logistics expertise. The Tri-City has a growing but less saturated tech market.' },
      { question: 'What is the cost vs Western Europe?', answer: 'Senior developers at €20-35/hr vs €80-120/hr in Western Europe. 65%+ savings.' },
      { question: 'Do you have maritime software experience?', answer: 'Yes. Port management, vessel tracking, maritime logistics, and supply chain platforms.' },
      { question: 'Is Poland GDPR compliant?', answer: 'Absolutely. EU member state. Full GDPR compliance and EU data residency.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs in 5 days, first sprint in week 3.' },
      { question: 'Do you support Scandinavian companies?', answer: 'Yes. Gdańsk\'s proximity to Scandinavia makes it ideal. Many engineers have Nordic project experience.' },
    ],
    localRate: '€80-120',
    ourRate: '€20-35',
    savings: '65%+',
  },

  'sofia-hub2': {
    slug: 'software-development-agency-sofia-hub2',
    cityName: 'Sofia',
    countryName: 'Bulgaria',
    flag: '🇧🇬',
    timezone: 'EET (GMT+2)',
    currency: 'BGN/EUR',
    title: {
      en: 'Software Development Agency Sofia | Call IT Dev',
      fr: 'Agence de Développement Informatique Sofia | Call IT Dev',
      de: 'Softwareentwicklung Agentur Sofia | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Sofia, Bulgaria. SaaS, web, mobile, AI. EET timezone, from €20/hr. 70% savings. EU\'s most cost-effective tech hub.',
      fr: 'Agence de développement informatique pour Sofia. SaaS, web, mobile, IA. Fuseau EET, dès 20 €/h. 70% d\'économies.',
      de: 'Softwareentwicklungsagentur für Sofia, Bulgarien. SaaS, Web, Mobile, KI. EET-Zeitzone, ab 20 €/h. 70% Ersparnis.',
    },
    keywords: ['software development agency Sofia', 'IT outsourcing Bulgaria', 'nearshore development Sofia', 'software house Bulgaria', 'offshore development Sofia'],
    heroTitle: {
      en: 'Software Development Agency for Sofia',
      fr: 'Agence de Développement Informatique pour Sofia',
      de: 'Softwareentwicklung Agentur für Sofia',
    },
    heroSubtitle: {
      en: 'EU\'s most cost-effective tech hub. EET timezone, 70% savings, 10% flat tax. Bulgaria\'s booming IT sector.',
      fr: 'Hub tech le plus rentable de l\'UE. Fuseau EET, 70% d\'économies, flat tax 10%. Secteur IT bulgare en plein essor.',
      de: 'Kostengünstigster Tech-Hub der EU. EET-Zeitzone, 70% Ersparnis, 10% Flat Tax. Bulgariens boomender IT-Sektor.',
    },
    whyTitle: {
      en: 'Why Choose Sofia for Software Development',
      fr: 'Pourquoi Choisir Sofia pour le Développement Logiciel',
      de: 'Warum Sofia für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '💰', title: 'EU\'s Best Value', description: 'Bulgaria has the EU\'s lowest cost of living with 10% flat tax. Exceptional developer value.' },
      { icon: '⏰', title: 'EET Timezone (GMT+2)', description: '1 hour ahead of CET. Full overlap with European business hours.' },
      { icon: '🎓', title: 'Strong STEM Education', description: 'Sofia University and Technical University produce 8K+ IT graduates annually.' },
      { icon: '🇪🇺', title: 'EU Member Since 2007', description: 'Full GDPR compliance, EU data residency, stable democratic framework.' },
      { icon: '🌐', title: 'Multilingual Engineers', description: 'English, German, Russian, and French-speaking developers available.' },
      { icon: '🏗️', title: 'Tech Park Sofia', description: 'Government-backed tech parks and innovation hubs with modern infrastructure.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Sofia over Romania or Poland?', answer: 'Bulgaria offers the EU\'s lowest costs with 10% flat tax. Less talent competition means lower attrition rates.' },
      { question: 'What is the cost vs Western Europe?', answer: 'Senior developers at €20-35/hr vs €80-120/hr in Western Europe. 70%+ savings.' },
      { question: 'Is Bulgaria GDPR compliant?', answer: 'Yes. EU member since 2007. Full GDPR compliance and EU data residency.' },
      { question: 'What languages do engineers speak?', answer: 'English (primary), plus German, Russian, and French. Many engineers have worked for Western European companies.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Do you support blockchain projects?', answer: 'Yes. Sofia has a growing blockchain community. Experience with Ethereum, Solana, and DeFi platforms.' },
    ],
    localRate: '€80-120',
    ourRate: '€20-35',
    savings: '70%+',
  },

  'vilnius-hub': {
    slug: 'software-development-agency-vilnius-hub',
    cityName: 'Vilnius',
    countryName: 'Lithuania',
    flag: '🇱🇹',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Vilnius | Call IT Dev',
      fr: 'Agence de Développement Informatique Vilnius | Call IT Dev',
      de: 'Softwareentwicklung Agentur Vilnius | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Vilnius, Lithuania. SaaS, web, mobile, AI. Baltic FinTech leader, EET timezone, from €25/hr. 60% savings.',
      fr: 'Agence de développement informatique pour Vilnius. SaaS, web, mobile, IA. Leader FinTech balte, dès 25 €/h. 60% d\'économies.',
      de: 'Softwareentwicklungsagentur für Vilnius, Litauen. SaaS, Web, Mobile, KI. Baltischer FinTech-Leader, ab 25 €/h. 60% Ersparnis.',
    },
    keywords: ['software development agency Vilnius', 'IT outsourcing Lithuania', 'nearshore development Vilnius', 'FinTech development Vilnius', 'software house Lithuania'],
    heroTitle: {
      en: 'Software Development Agency for Vilnius',
      fr: 'Agence de Développement Informatique pour Vilnius',
      de: 'Softwareentwicklung Agentur für Vilnius',
    },
    heroSubtitle: {
      en: 'Europe\'s FinTech licensing capital. EET timezone, 230+ licensed FinTechs, 60% savings. Lithuania\'s booming tech ecosystem.',
      fr: 'Capitale européenne des licences FinTech. Fuseau EET, 230+ FinTechs licenciées, 60% d\'économies.',
      de: 'Europas FinTech-Lizenzhauptstadt. EET-Zeitzone, 230+ lizenzierte FinTechs, 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Choose Vilnius for Software Development',
      fr: 'Pourquoi Choisir Vilnius pour le Développement Logiciel',
      de: 'Warum Vilnius für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '🏦', title: 'FinTech Capital of Europe', description: '230+ licensed FinTechs. Revolut, Vinted, and dozens of unicorns have Lithuanian roots.' },
      { icon: '⏰', title: 'EET Timezone', description: 'GMT+2 — excellent overlap with CET and Nordic business hours.' },
      { icon: '💰', title: '60% Savings', description: 'Senior developers at €25-40/hr vs €80-130/hr in Nordic countries.' },
      { icon: '🇪🇺', title: 'EU & Eurozone', description: 'Full GDPR compliance, EUR currency, EU passporting for financial services.' },
      { icon: '🎓', title: 'Vilnius University Talent', description: 'Top-ranked university with strong CS programs. 6K+ IT graduates annually.' },
      { icon: '🌐', title: 'English Proficiency', description: 'Lithuania ranks among top 10 globally for English proficiency.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Vilnius for FinTech?', answer: '230+ licensed FinTechs. Lithuania\'s Central Bank offers the fastest FinTech licensing in the EU. Deep regulatory expertise.' },
      { question: 'What is the cost vs Nordic countries?', answer: 'Senior devs at €25-40/hr vs €80-130/hr in Stockholm or Helsinki. 60%+ savings.' },
      { question: 'Is Lithuania GDPR compliant?', answer: 'Yes. EU member, Eurozone since 2015. Full GDPR compliance and EU passporting.' },
      { question: 'Do engineers speak English?', answer: 'Yes. Lithuania ranks top 10 globally for English proficiency. All engineers communicate fluently.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Do you support open banking projects?', answer: 'Yes. Deep PSD2 and open banking expertise. Experience building banking APIs and payment infrastructure.' },
    ],
    localRate: '€80-130',
    ourRate: '€25-40',
    savings: '60%+',
  },

  pune: {
    slug: 'software-development-agency-pune',
    cityName: 'Pune',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Pune | Call IT Dev',
      fr: 'Agence de Développement Informatique Pune | Call IT Dev',
      de: 'Softwareentwicklung Agentur Pune | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Pune, Maharashtra. SaaS, web, mobile, AI. India\'s #2 IT city, IST timezone, from $18/hr. 75% savings vs US rates.',
      fr: 'Agence de développement informatique pour Pune. SaaS, web, mobile, IA. 2e ville IT d\'Inde, dès 18 $/h. 75% d\'économies.',
      de: 'Softwareentwicklungsagentur für Pune, Maharashtra. SaaS, Web, Mobile, KI. Indiens #2 IT-Stadt, ab 18 $/h. 75% Ersparnis.',
    },
    keywords: ['software development agency Pune', 'IT outsourcing Pune', 'offshore development Pune', 'software company Pune', 'nearshore development India'],
    heroTitle: {
      en: 'Software Development Agency for Pune',
      fr: 'Agence de Développement Informatique pour Pune',
      de: 'Softwareentwicklung Agentur für Pune',
    },
    heroSubtitle: {
      en: 'India\'s Oxford of the East. 800+ IT companies, IST timezone, 75% savings vs US. Automotive, FinTech & enterprise software powerhouse.',
      fr: 'L\'Oxford de l\'Est de l\'Inde. 800+ entreprises IT, fuseau IST, 75% d\'économies. Automobile, FinTech & logiciel d\'entreprise.',
      de: 'Indiens Oxford des Ostens. 800+ IT-Unternehmen, IST-Zeitzone, 75% Ersparnis. Automobil, FinTech & Enterprise-Software.',
    },
    whyTitle: {
      en: 'Why Choose Pune for Software Development',
      fr: 'Pourquoi Choisir Pune pour le Développement Logiciel',
      de: 'Warum Pune für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'Education Capital', description: 'Pune has 100+ engineering colleges. Home to prestigious institutes producing top CS talent.' },
      { icon: '💰', title: '75% Cost Savings', description: 'Senior developers at $18-30/hr vs $80-150/hr in the US. Exceptional value.' },
      { icon: '🚗', title: 'Automotive & Manufacturing', description: 'Pune is India\'s Detroit. Deep expertise in automotive software, IoT, and Industry 4.0.' },
      { icon: '🏢', title: '800+ IT Companies', description: 'Infosys, TCS, Wipro, and 800+ IT companies. Massive, experienced talent pool.' },
      { icon: '🌐', title: 'English Fluent', description: 'Pune\'s educated workforce is fully English-proficient. Clear communication guaranteed.' },
      { icon: '📈', title: 'Startup Ecosystem', description: 'Growing startup scene with 1000+ active startups. Engineers experienced with rapid product development.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Pune vs Bangalore?', answer: 'Pune offers 15-20% lower costs than Bangalore with similar talent quality. Less traffic, better quality of life, lower attrition.' },
      { question: 'What is the cost vs US?', answer: 'Senior developers at $18-30/hr vs $80-150/hr in the US. 75%+ savings.' },
      { question: 'Do you have automotive expertise?', answer: 'Yes. Pune is India\'s auto capital. We build automotive IoT, connected car, and smart factory solutions.' },
      { question: 'How do you manage timezone?', answer: 'IST is 5.5h ahead of CET. Flexible overlapping hours for real-time syncs. Async workflows for non-overlapping time.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days, first sprint in week 3.' },
      { question: 'Is data secure?', answer: 'Enterprise-grade security. All engineers sign NDAs. Encrypted dev environments and communication.' },
    ],
    localRate: '$80-150',
    ourRate: '$18-30',
    savings: '75%+',
  },

  'jakarta-hub': {
    slug: 'software-development-agency-jakarta-hub',
    cityName: 'Jakarta',
    countryName: 'Indonesia',
    flag: '🇮🇩',
    timezone: 'WIB (GMT+7)',
    currency: 'IDR/USD',
    title: {
      en: 'Software Development Agency Jakarta | Call IT Dev',
      fr: 'Agence de Développement Informatique Jakarta | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Jakarta, Indonesia. SaaS, web, mobile, AI. Southeast Asia\'s largest tech market, from $15/hr. 80% savings vs US rates.',
      fr: 'Agence de développement informatique pour Jakarta. SaaS, web, mobile, IA. Plus grand marché tech d\'Asie du Sud-Est, dès 15 $/h. 80% d\'économies.',
    },
    keywords: ['software development agency Jakarta', 'IT outsourcing Indonesia', 'software house Jakarta', 'nearshore development Southeast Asia', 'app development Jakarta'],
    heroTitle: {
      en: 'Software Development Agency for Jakarta',
      fr: 'Agence de Développement Informatique pour Jakarta',
    },
    heroSubtitle: {
      en: 'Southeast Asia\'s digital powerhouse. 270M-population market, mobile-first engineering, 80% savings. Gojek, Tokopedia, and Traveloka\'s home turf.',
      fr: 'Puissance numérique d\'Asie du Sud-Est. Marché de 270M d\'habitants, ingénierie mobile-first, 80% d\'économies.',
    },
    whyTitle: {
      en: 'Why Choose Jakarta for Software Development',
      fr: 'Pourquoi Choisir Jakarta pour le Développement Logiciel',
    },
    whyReasons: [
      { icon: '📱', title: 'Mobile-First Expertise', description: 'Indonesia is mobile-first. Engineers excel in mobile apps, super-apps, and mobile commerce platforms.' },
      { icon: '💰', title: '80% Cost Savings', description: 'Senior developers at $15-28/hr vs $80-150/hr in the US. Top value in Southeast Asia.' },
      { icon: '🌏', title: '270M Market', description: 'World\'s 4th largest population. Engineers understand massive-scale, high-traffic systems.' },
      { icon: '🦄', title: 'Unicorn Ecosystem', description: 'Home to Gojek, Tokopedia, Traveloka. Engineers trained in world-class product development.' },
      { icon: '💳', title: 'FinTech & E-commerce', description: 'Deep expertise in digital payments, e-wallets, and marketplace platforms.' },
      { icon: '🌐', title: 'English-Proficient', description: 'Growing English proficiency in tech sector. Bilingual teams available.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Jakarta for software development?', answer: 'World\'s 4th largest population, booming digital economy, unicorn ecosystem. Engineers experienced with massive-scale systems.' },
      { question: 'What is the cost vs US?', answer: 'Senior developers at $15-28/hr vs $80-150/hr in the US. 80%+ savings.' },
      { question: 'Do you have e-commerce expertise?', answer: 'Yes. Indonesia\'s e-commerce market is Southeast Asia\'s largest. Deep marketplace and logistics platform experience.' },
      { question: 'How do you manage timezone?', answer: 'WIB (GMT+7) — 6 hours ahead of CET. Morning syncs with European teams, afternoon overlap with Asian markets.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Do you support super-app development?', answer: 'Yes. Experience building multi-service platforms inspired by Gojek and Grab. Ride-hailing, food delivery, payments integrated.' },
    ],
    localRate: '$80-150',
    ourRate: '$15-28',
    savings: '80%+',
  },

  'santiago-hub': {
    slug: 'software-development-agency-santiago-hub',
    cityName: 'Santiago',
    countryName: 'Chile',
    flag: '🇨🇱',
    timezone: 'CLT (GMT-3)',
    currency: 'CLP/USD',
    title: {
      en: 'Software Development Agency Santiago | Call IT Dev',
      fr: 'Agence de Développement Informatique Santiago | Call IT Dev',
      es: 'Agencia Desarrollo Software Santiago de Chile | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Santiago, Chile. SaaS, web, mobile, AI. LATAM\'s startup capital, GMT-3, from $22/hr. 70% savings vs US rates.',
      fr: 'Agence de développement informatique pour Santiago. SaaS, web, mobile, IA. Capitale startup de LATAM, dès 22 $/h. 70% d\'économies.',
      es: 'Agencia de desarrollo de software en Santiago, Chile. SaaS, web, móvil, IA. Capital startup de LATAM, GMT-3, desde 22 $/h.',
    },
    keywords: ['software development agency Santiago', 'IT outsourcing Chile', 'nearshore development Santiago', 'software house Chile', 'desarrollo software Santiago'],
    heroTitle: {
      en: 'Software Development Agency for Santiago',
      fr: 'Agence de Développement Informatique pour Santiago',
      es: 'Agencia de Desarrollo de Software en Santiago de Chile',
    },
    heroSubtitle: {
      en: 'LATAM\'s most business-friendly tech hub. GMT-3 timezone, US overlap, 70% savings. Chile\'s startup-friendly regulations and stable economy.',
      fr: 'Hub tech le plus business-friendly de LATAM. Fuseau GMT-3, chevauchement US, 70% d\'économies.',
      es: 'El hub tech más business-friendly de LATAM. Zona GMT-3, superposición US, 70% ahorro. Regulaciones pro-startup de Chile.',
    },
    whyTitle: {
      en: 'Why Choose Santiago for Software Development',
      fr: 'Pourquoi Choisir Santiago pour le Développement Logiciel',
      es: 'Por Qué Elegir Santiago para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '🏆', title: 'LATAM\'s #1 Business Climate', description: 'Chile consistently ranked #1 in LATAM for ease of doing business. Stable economy and transparent regulations.' },
      { icon: '⏰', title: 'US Timezone Overlap', description: 'GMT-3 — full overlap with US East Coast. Real-time collaboration with American teams.' },
      { icon: '💰', title: '70% vs US Rates', description: 'Senior developers at $22-38/hr vs $100-180/hr in the US.' },
      { icon: '🎓', title: 'Universidad de Chile Talent', description: 'Top-ranked university in LATAM. Strong CS and engineering programs.' },
      { icon: '🌐', title: 'Bilingual EN/ES', description: 'Engineers fluent in English and Spanish. Ideal for US and LATAM markets.' },
      { icon: '🚀', title: 'Startup Chile Program', description: 'Government-backed startup accelerator attracts global talent and innovation.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Santiago over Buenos Aires?', answer: 'Chile has LATAM\'s most stable economy, strongest IP protection, and most business-friendly regulations.' },
      { question: 'What is the cost vs US?', answer: 'Senior developers at $22-38/hr vs $100-180/hr in the US. 70%+ savings with timezone overlap.' },
      { question: 'Do engineers speak English?', answer: 'Yes. Chilean tech professionals have strong English proficiency. Bilingual EN/ES teams standard.' },
      { question: 'What industries are strong in Santiago?', answer: 'FinTech, mining tech, renewable energy, e-commerce, and SaaS. Chile leads LATAM in solar energy adoption.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days, first sprint in week 3.' },
      { question: 'Is data protected?', answer: 'Yes. Chile has LATAM\'s strongest data protection laws. Enterprise-grade security infrastructure.' },
    ],
    localRate: '$100-180',
    ourRate: '$22-38',
    savings: '70%+',
  },

  'kigali-hub': {
    slug: 'software-development-agency-kigali-hub',
    cityName: 'Kigali',
    countryName: 'Rwanda',
    flag: '🇷🇼',
    timezone: 'CAT (GMT+2)',
    currency: 'RWF/USD',
    title: {
      en: 'Software Development Agency Kigali | Call IT Dev',
      fr: 'Agence de Développement Informatique Kigali | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Kigali, Rwanda. SaaS, web, mobile, AI. Africa\'s smartest city, CAT timezone, from $15/hr. 80% savings.',
      fr: 'Agence de développement informatique pour Kigali. SaaS, web, mobile, IA. Ville la plus smart d\'Afrique, dès 15 $/h. 80% d\'économies.',
    },
    keywords: ['software development agency Kigali', 'IT outsourcing Rwanda', 'nearshore development Kigali', 'software house Rwanda', 'tech hub East Africa'],
    heroTitle: {
      en: 'Software Development Agency for Kigali',
      fr: 'Agence de Développement Informatique pour Kigali',
    },
    heroSubtitle: {
      en: 'Africa\'s Silicon Valley. CAT timezone (GMT+2), drone delivery pioneer, 80% savings. Rwanda\'s digital-first government drives tech innovation.',
      fr: 'La Silicon Valley africaine. Fuseau CAT (GMT+2), pionnier de la livraison par drones, 80% d\'économies.',
    },
    whyTitle: {
      en: 'Why Choose Kigali for Software Development',
      fr: 'Pourquoi Choisir Kigali pour le Développement Logiciel',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Africa\'s Smartest City', description: 'Kigali ranked Africa\'s cleanest, safest, and most innovative city. Government-backed tech transformation.' },
      { icon: '💰', title: '80% Cost Savings', description: 'Senior developers at $15-25/hr vs $80-150/hr in Western markets.' },
      { icon: '🌐', title: 'Trilingual Talent', description: 'Engineers fluent in English, French, and Kinyarwanda. Ideal for anglophone and francophone markets.' },
      { icon: '🤖', title: 'Innovation Pioneer', description: 'First country with drone delivery (Zipline), cashless payments, and e-governance.' },
      { icon: '⏰', title: 'CAT Timezone (GMT+2)', description: '1 hour ahead of CET. Excellent overlap with European business hours.' },
      { icon: '📡', title: '4G LTE Coverage', description: '95%+ 4G coverage. Government investment in fiber optic and tech infrastructure.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Kigali for software development?', answer: 'Rwanda has Africa\'s most business-friendly environment. Kigali combines low costs, trilingual talent, and government-backed tech infrastructure.' },
      { question: 'What is the cost vs Western agencies?', answer: 'Senior developers at $15-25/hr vs $80-150/hr in Western markets. 80%+ savings.' },
      { question: 'Do engineers speak English and French?', answer: 'Yes. Rwanda is trilingual (English, French, Kinyarwanda). Engineers communicate fluently in both English and French.' },
      { question: 'Is internet connectivity reliable?', answer: 'Yes. Rwanda has 95%+ 4G coverage and growing fiber optic infrastructure. Our offices have redundant connectivity.' },
      { question: 'What about data security?', answer: 'Rwanda has enacted comprehensive data protection legislation. Enterprise-grade security infrastructure.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days.' },
    ],
    localRate: '$80-150',
    ourRate: '$15-25',
    savings: '80%+',
  },

  'kuwait-city-hub': {
    slug: 'software-development-agency-kuwait-city-hub',
    cityName: 'Kuwait City',
    countryName: 'Kuwait',
    flag: '🇰🇼',
    timezone: 'AST (GMT+3)',
    currency: 'KWD/USD',
    title: {
      en: 'Software Development Agency Kuwait City | Call IT Dev',
      fr: 'Agence de Développement Informatique Koweït | Call IT Dev',
      ar: 'وكالة تطوير برمجيات الكويت | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Kuwait City. SaaS, web, mobile, AI. Gulf oil & gas tech hub, AST timezone, from $25/hr. 65% savings vs local rates.',
      fr: 'Agence de développement informatique pour Koweït. SaaS, web, mobile, IA. Hub tech pétrolier du Golfe, dès 25 $/h. 65% d\'économies.',
      ar: 'وكالة تطوير برمجيات لمدينة الكويت. SaaS، ويب، موبايل، ذكاء اصطناعي. مركز تقني للنفط والغاز في الخليج، من 25$/ساعة.',
    },
    keywords: ['software development agency Kuwait', 'IT outsourcing Kuwait', 'software house Kuwait City', 'nearshore development Gulf', 'oil gas tech Kuwait'],
    heroTitle: {
      en: 'Software Development Agency for Kuwait City',
      fr: 'Agence de Développement Informatique pour Koweït',
      ar: 'وكالة تطوير برمجيات لمدينة الكويت',
    },
    heroSubtitle: {
      en: 'Gulf\'s oil & gas tech transformation. AST timezone, Arabic & English engineers, 65% savings. Kuwait Vision 2035 digital partner.',
      fr: 'Transformation tech pétrolière du Golfe. Fuseau AST, ingénieurs arabophones & anglophones, 65% d\'économies.',
      ar: 'التحول التقني في قطاع النفط والغاز الخليجي. منطقة AST، مهندسون عرب وإنجليز، توفير 65%. شريك رقمي لرؤية الكويت 2035.',
    },
    whyTitle: {
      en: 'Why Choose Kuwait City for Software Development',
      fr: 'Pourquoi Choisir Koweït pour le Développement Logiciel',
      ar: 'لماذا تختار مدينة الكويت لتطوير البرمجيات',
    },
    whyReasons: [
      { icon: '🛢️', title: 'Oil & Gas Digital Transformation', description: 'Kuwait\'s energy sector digitalization drives demand for IoT, AI, and enterprise software.' },
      { icon: '⏰', title: 'AST Timezone (GMT+3)', description: 'Bridges European and Asian business hours. Good overlap with both markets.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Developers at $25-40/hr vs $70-120/hr locally. Enterprise quality at competitive rates.' },
      { icon: '🗣️', title: 'Arabic & English', description: 'Bilingual teams for Gulf and international markets. Native Arabic speakers available.' },
      { icon: '🏗️', title: 'Kuwait Vision 2035', description: 'Government digital transformation initiative creating massive demand for tech solutions.' },
      { icon: '🔒', title: 'Data Protection', description: 'Compliance with Kuwait\'s data protection framework and enterprise-grade security.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have oil & gas software experience?', answer: 'Yes. Asset management, IoT monitoring, predictive maintenance, and HSE compliance platforms for energy companies.' },
      { question: 'What is the cost vs Kuwait agencies?', answer: 'Developers at $25-40/hr vs $70-120/hr locally. 65%+ savings.' },
      { question: 'Do your engineers speak Arabic?', answer: 'Yes. Bilingual Arabic/English teams for all Gulf projects.' },
      { question: 'Can you support Kuwait Vision 2035?', answer: 'Yes. We build e-government, smart city, and digital transformation platforms aligned with Kuwait\'s national strategy.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days, first sprint in week 3.' },
      { question: 'Is data secure?', answer: 'Enterprise-grade security. Full compliance with Kuwait data protection regulations.' },
    ],
    localRate: '$70-120',
    ourRate: '$25-40',
    savings: '65%+',
  },
};
